import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToShortstayData } from "../../../../redux/shortstaysSlice";
import {
  listenToListingReservations,
  listenToShortstayReservaionData,
} from "../../../../redux/shortstayReservationSlice";
import { fetchAllUsers } from "../../../../redux/authSlice";
import {
  ChevronLeft, ChevronRight, X, AlertTriangle,
  Calendar, MapPin, Clock, MessageCircle, Users,
} from "lucide-react";
import axios from "axios";
import { makeThreadId } from "../../../../redux/iMessageSlice";

/* ── tokens ── */
const T = {
  bg:         "#F7F5EF",
  surface:    "#ffffff",
  border:     "#E8E5DC",
  border2:    "#D3D1C7",
  text:       "#2C2C2A",
  textSoft:   "#5F5E5A",
  muted:      "#888780",
  gold:       "#BA7517",
  goldLight:  "#FAEEDA",
  goldBorder: "#FAC775",
  red:        "#C0392B",
  redLight:   "#FDEEEC",
  redBorder:  "#F5B7B1",
  sans:       "'Montserrat', sans-serif",
  serif:      "'Cormorant Garamond', serif",
};

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300&family=Montserrat:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  input, textarea, button { font-family: 'Montserrat', sans-serif; }

  .hcb-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    align-items: start;
    margin-top: 16px;
  }
  .hcb-col { display: flex; flex-direction: column; gap: 12px; }

  @media (max-width: 820px) {
    .hcb-layout { grid-template-columns: 1fr; }
  }

  .msg-btn:hover {
    background: ${T.bg} !important;
    border-color: ${T.gold} !important;
    color: ${T.gold} !important;
  }
`;

/* ── date helpers — strips timezone, treats as local midnight ── */
const parseISODate = (iso: string): Date => {
  if (!iso) return new Date();
  const clean = iso.split("T")[0];                         // "2026-06-01"
  const [y, m, d] = clean.split("-").map(Number);
  return new Date(y, m - 1, d);                            // local, no UTC offset
};

const formatDate = (iso: string): string => {
  if (!iso) return "—";
  return parseISODate(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",        // "1 June 2026"
  });
};

const formatDateShort = (iso: string): string => {
  if (!iso) return "—";
  return parseISODate(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short",                        // "1 Jun"
  });
};

const diffNights = (a: Date, b: Date) =>
  Math.round((b.getTime() - a.getTime()) / 86400000);

type Step = "view" | "confirm";

/* ════════════════════════════════════════════════════════════ */
const HostCancelShortBooking: React.FC = () => {
  const { hostId, listingId, reservationId } = useParams<{
    hostId: string; listingId: string; reservationId: string;
  }>();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const listing      = useSelector((s: RootState) => s.shortstays.currentListing);
  const reservations = useSelector((s: RootState) => s.shortstayReservations.listingReservations);
  const allUsers     = useSelector((s: RootState) => s.auth.allUsers);
const authUser = useSelector((s: RootState) => s.auth.user);

  const booking = reservations.find((r) => r.id === reservationId);
  const guest   = booking ? allUsers.find((u) => u.userId === booking.guestId) : null;
  const guestId = guest?.userId || booking?.guestId;

  const [step,               setStep]               = useState<Step>("view");
  const [monthOffset,        setMonthOffset]        = useState(0);
  const [cancellationReason, setCancellationReason] = useState("");
  const [loading,            setLoading]            = useState(false);

  useEffect(() => {
    if (listingId) {
      dispatch(listenToShortstayData(listingId));
      dispatch(listenToListingReservations(listingId));
    }
    if (reservationId) dispatch(listenToShortstayReservaionData(reservationId));
    dispatch(fetchAllUsers());
  }, [dispatch, listingId, reservationId]);

  /* auto-jump calendar to the booking's month */
  useEffect(() => {
    if (!booking) return;
    const checkIn = parseISODate(booking.checkIn);
    const today   = new Date();
    const diff =
      (checkIn.getFullYear() - today.getFullYear()) * 12 +
      (checkIn.getMonth() - today.getMonth());
    setMonthOffset(Math.max(0, diff));
  }, [booking]);

  /* ── derived ── */
  const today           = new Date();
  const currentResStart = booking ? parseISODate(booking.checkIn)  : null;
  const currentResEnd   = booking ? parseISODate(booking.checkOut) : null;
  const nights          = currentResStart && currentResEnd
    ? diffNights(currentResStart, currentResEnd) : 0;

  const currentViewDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const currentMonth    = currentViewDate.toLocaleString("default", { month: "long" });
  const currentYear     = currentViewDate.getFullYear();

  /* is the entire viewed month in the past? */
  const isPastMonth =
    currentViewDate.getFullYear() < today.getFullYear() ||
    (currentViewDate.getFullYear() === today.getFullYear() &&
     currentViewDate.getMonth()    < today.getMonth());

  /* reserved / blocked sets */
  const blockedDates = new Set<string>(
    (listing?.blockedDuration || []).map((d: string) => parseISODate(d).toDateString())
  );
  const reservedDates = new Set<string>();
  reservations.forEach((res) => {
    if (res.isCancelled) return;
    const start = parseISODate(res.checkIn);
    const end   = parseISODate(res.checkOut);
    for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1))
      reservedDates.add(new Date(d).toDateString());
  });

  const getMonthDates = (): Date[] => {
    const firstDay = new Date(currentViewDate.getFullYear(), currentViewDate.getMonth(), 1);
    const startDay = (firstDay.getDay() + 6) % 7;
    const start    = new Date(firstDay);
    start.setDate(start.getDate() - startDay);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i); return d;
    });
  };

  /* ── cancel ── */
  const confirmCancel = async () => {
    if (!booking || !listing || !hostId || !guestId) return;
    setLoading(true);
    try {
      await axios.post("https://areluxe.com/applyShortRefund", {
        reservationId: booking.id, hostId, guestId, listingId,
        dateKey: booking.checkIn, cancellationType: "host", cancellationReason,
      });
      navigate(`/shortdashboard/${hostId}`);
    } catch (err: any) {
      alert(err?.response?.data?.message || "Failed to cancel. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ── day cell ── */
  const renderDayCell = (date: Date, i: number) => {
    const dateStr      = date.toDateString();
    const isOtherMonth = date.getMonth() !== currentViewDate.getMonth();
    const isCheckIn    = !!currentResStart && dateStr === currentResStart.toDateString();
    const isCheckOut   = !!currentResEnd   && dateStr === currentResEnd.toDateString();
    const isInBooking  = !!currentResStart && !!currentResEnd &&
                         date > currentResStart && date < currentResEnd;
    const isToday      = dateStr === today.toDateString();
    const isPast       = date < today;

    /* entirely grey out past-month cells (unless part of booking) */
    if (isPastMonth && !isCheckIn && !isCheckOut && !isInBooking) {
      return (
        <div key={i} style={{
          padding: "7px 2px 5px", textAlign: "center", borderRadius: 8,
          opacity: isOtherMonth ? 0.1 : 0.3,
        }}>
          <span style={{ fontSize: 12, color: T.border2 }}>{date.getDate()}</span>
        </div>
      );
    }

    let bg    = "transparent";
    let color = T.text;
    let fontW = 400 as number;
    let radius = "8px";
    let sub: React.ReactNode = null;

    if (isCheckIn) {
      bg = T.gold; color = "#fff"; fontW = 600; radius = "8px 0 0 8px";
      sub = <span style={{ display: "block", fontSize: 7, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" as const, color: "rgba(255,255,255,.75)", marginTop: 2 }}>In</span>;
    } else if (isCheckOut) {
      bg = T.goldLight; color = T.gold; fontW = 600; radius = "0 8px 8px 0";
      sub = <span style={{ display: "block", fontSize: 7, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" as const, color: T.gold, marginTop: 2 }}>Out</span>;
    } else if (isInBooking) {
      bg = T.goldLight; color = T.text; radius = "0";
    } else if (blockedDates.has(dateStr) || reservedDates.has(dateStr)) {
      color = T.border2;
    }

    if (isPast && !isCheckIn && !isCheckOut && !isInBooking) color = T.border2;

    return (
      <div key={i} style={{
        padding: "7px 2px 5px", textAlign: "center", borderRadius: radius,
        background: bg, opacity: isOtherMonth ? 0.2 : 1,
        outline: isToday && !isCheckIn && !isInBooking ? `2px solid ${T.goldBorder}` : "none",
        outlineOffset: -1,
      }}>
        <span style={{ fontSize: 12, fontWeight: fontW, color, lineHeight: 1 }}>{date.getDate()}</span>
        {sub}
      </div>
    );
  };

  if (!booking && reservations.length > 0) {
    return (
      <div style={{ ...s.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <style>{FONT_CSS}</style>
        <p style={{ color: T.muted, fontSize: 14 }}>Reservation not found.</p>
      </div>
    );
  }

  /* ─── total charged display ─── */
  const totalDisplay = (() => {
    const raw = booking?.totalCharged ?? booking?.totalPrice;
    if (!raw) return "—";
    return `€${Number(raw).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
  })();

  const guestCount = booking?.guestCount ?? booking?.guests ?? "—";

  /* ════════════════════ RENDER ════════════════════ */
  return (
    <div style={s.page}>
      <style>{FONT_CSS}</style>

      <div style={s.wrap}>

        {/* back */}
        <button onClick={() => navigate(-1)} style={s.backBtn}>
          <ChevronLeft size={16} /> Back
        </button>

        <p style={s.stepLabel}>Reservation management</p>
        <h1 style={s.pageTitle}>
          {step === "view" ? "Manage reservation" : "Confirm cancellation"}
        </h1>

        {/* ── listing hero — full width ── */}
        <div style={s.heroPhoto}>
          {listing?.photos?.[0]
            ? <img src={listing.photos[0]} alt="Listing" style={s.heroImg} />
            : <div style={s.heroPlaceholder}><MapPin size={24} color={T.muted} /></div>
          }
          <div style={s.heroOverlay}>
            <p style={s.heroListingName}>{listing?.title || "Your listing"}</p>
            {listing?.address && <p style={s.heroAddress}>{[listing.address.street, listing.address.city, listing.address.country].filter(Boolean).join(", ")}</p>}
          </div>
        </div>

        {/* ── 2-col desktop / 1-col mobile ── */}
        <div className="hcb-layout">

          {/* LEFT: guest card + booking summary */}
          <div className="hcb-col">

            {/* guest card */}
            <div style={s.card}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <img
                    src={guest?.profilePicture || "/placeholder.jpg"}
                    alt="Guest"
                    style={s.guestAvatar}
                    onClick={() => guest && navigate(`/aboutme/${guest.userId}`)}
                  />
                  <span style={s.onlineDot} />
                </div>
                <div>
                  <p style={{ fontSize: 15, fontWeight: 600, color: T.text }}>
                    {guest ? `${guest.firstName} ${guest.lastName}` : "Loading…"}
                  </p>
                  <p style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Booked guest</p>
                </div>
              </div>

              {/* chips */}
              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 7, marginBottom: 14 }}>
                <div style={s.chip}>
                  <Calendar size={11} color={T.gold} />
                  <span>{formatDateShort(booking?.checkIn ?? "")} → {formatDateShort(booking?.checkOut ?? "")}</span>
                </div>
                <div style={{ ...s.chip, background: T.goldLight, borderColor: T.goldBorder }}>
                  <Clock size={11} color={T.gold} />
                  <span style={{ color: T.gold, fontWeight: 600 }}>{nights} night{nights !== 1 ? "s" : ""}</span>
                </div>
                <div style={s.chip}>
                  <Users size={11} color={T.muted} />
                  <span>{guestCount} guest{Number(guestCount) !== 1 ? "s" : ""}</span>
                </div>
              </div>

              {/* message button */}
              <button
                className="msg-btn"
onClick={() => authUser && guestId
  ? navigate(`/imessages/${makeThreadId(authUser.userId, guestId)}`)
  : navigate("/AuthSwitch")
}                style={s.messageBtn}
              >
                <MessageCircle size={14} />
                Message {guest?.firstName ?? "guest"}
              </button>
            </div>

            {/* booking summary */}
            <div style={s.card}>
              <p style={s.cardSectionLabel}>Booking details</p>
              {[
                { label: "Check-in",       val: formatDate(booking?.checkIn  ?? ""),  gold: false },
                { label: "Check-out",      val: formatDate(booking?.checkOut ?? ""),  gold: false },
                { label: "Guests",         val: String(guestCount),                   gold: false },
                { label: "Duration",       val: `${nights} night${nights !== 1 ? "s" : ""}`, gold: false },
                { label: "Total charged",  val: totalDisplay,                         gold: true  },
              ].map(({ label, val, gold }, i, arr) => (
                <div key={label} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "11px 0",
                  borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : "none",
                }}>
                  <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>{label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: gold ? T.gold : T.text }}>{val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: calendar + action */}
          <div className="hcb-col">

            {/* calendar */}
            <div style={s.card}>
              <div style={s.calHeader}>
                <button
                  style={s.chevBtn}
                  onClick={() => setMonthOffset(m => Math.max(0, m - 1))}
                  disabled={monthOffset === 0}
                >
                  <ChevronLeft size={14} color={monthOffset === 0 ? T.border2 : T.text} />
                </button>
                <span style={s.calMonth}>{currentMonth} {currentYear}</span>
                <button style={s.chevBtn} onClick={() => setMonthOffset(m => Math.min(18, m + 1))}>
                  <ChevronRight size={14} color={T.text} />
                </button>
              </div>

              <div style={s.calGrid}>
                {["Mo","Tu","We","Th","Fr","Sa","Su"].map(d => (
                  <div key={d} style={s.dayLabel}>{d}</div>
                ))}
                {getMonthDates().map((date, i) => renderDayCell(date, i))}
              </div>

              <div style={s.legend}>
                <div style={s.legendItem}><div style={{ ...s.legendDot, background: T.gold }} /><span>Check-in</span></div>
                <div style={s.legendItem}><div style={{ ...s.legendDot, background: T.goldLight, border: `1px solid ${T.goldBorder}` }} /><span>Stay</span></div>
                <div style={s.legendItem}><div style={{ ...s.legendDot, background: T.border2 }} /><span>Past</span></div>
              </div>
            </div>

            {/* action card */}
            <div style={s.card}>
              {booking?.isCancelled ? (
                <div style={s.cancelledBanner}>
                  <X size={15} color={T.muted} />
                  <span>This reservation has already been cancelled.</span>
                </div>
              ) : step === "view" ? (
                <>
                  <div style={s.warningBox}>
                    <AlertTriangle size={14} color={T.red} style={{ flexShrink: 0, marginTop: 1 }} />
                    <p style={s.warningText}>
                      Cancelling will trigger a penalty of <strong>twice your daily rate</strong> — one day
                      deducted and one paid to the guest as compensation.
                    </p>
                  </div>
                  <button onClick={() => setStep("confirm")} style={s.cancelBtn}>
                    Cancel this reservation
                  </button>
                </>
              ) : (
                <>
                  <p style={{ fontSize: 16, fontWeight: 600, color: T.text, fontFamily: T.serif, marginBottom: 4 }}>
                    Are you sure?
                  </p>
                  <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.5, marginBottom: 14 }}>
                    This cannot be undone. The guest will be refunded and a penalty applied.
                  </p>
                  <div style={s.warningBox}>
                    <AlertTriangle size={14} color={T.red} style={{ flexShrink: 0, marginTop: 1 }} />
                    <p style={s.warningText}>
                      ⚠️ Penalty: <strong>twice your listing's daily rate</strong> will be deducted.
                    </p>
                  </div>
                  <label style={s.textareaLabel}>
                    Reason <span style={{ color: T.muted, fontWeight: 400 }}>(optional)</span>
                  </label>
                  <textarea
                    style={s.textarea}
                    placeholder="e.g. Property maintenance, emergency…"
                    value={cancellationReason}
                    onChange={e => setCancellationReason(e.target.value)}
                    rows={3}
                  />
                  <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={() => setStep("view")} style={s.goBackBtn} disabled={loading}>
                      Go back
                    </button>
                    <button onClick={confirmCancel} style={s.confirmCancelBtn} disabled={loading}>
                      {loading ? "Processing…" : "Yes, cancel"}
                    </button>
                  </div>
                </>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default HostCancelShortBooking;

/* ── styles ── */
const s: Record<string, React.CSSProperties> = {
  page:      { background: T.bg, minHeight: "100vh", fontFamily: T.sans, color: T.text, paddingBottom: 80 },
  wrap:      { maxWidth: 1020, margin: "0 auto", padding: "24px 16px" },
  backBtn:   { display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "none", cursor: "pointer", fontSize: 13, color: T.muted, fontFamily: T.sans, marginBottom: 20, padding: 0 },
  stepLabel: { fontSize: 10, fontWeight: 500, color: T.muted, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6 },
  pageTitle: { fontSize: 28, fontWeight: 400, fontFamily: T.serif, color: T.text, marginBottom: 20, letterSpacing: "-0.01em" },

  heroPhoto:       { position: "relative", height: 220, borderRadius: 16, overflow: "hidden", background: T.border },
  heroImg:         { width: "100%", height: "100%", objectFit: "cover" },
  heroPlaceholder: { width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: T.border },
  heroOverlay:     { position: "absolute", bottom: 0, left: 0, right: 0, padding: "40px 20px 18px", background: "linear-gradient(to top, rgba(0,0,0,.6) 0%, transparent 100%)", borderRadius: "0 0 16px 16px" },
  heroListingName: { fontSize: 16, fontWeight: 600, color: "#fff" },
  heroAddress:     { fontSize: 12, color: "rgba(255,255,255,.7)", marginTop: 3 },

  card:             { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: "16px 16px 18px" },
  cardSectionLabel: { fontSize: 10, fontWeight: 600, color: T.muted, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12 },

  guestAvatar: { width: 52, height: 52, borderRadius: "50%", objectFit: "cover", border: `2px solid ${T.gold}`, cursor: "pointer", display: "block" },
  onlineDot:   { position: "absolute", bottom: 1, right: 1, width: 10, height: 10, borderRadius: "50%", background: "#27AE60", border: `2px solid ${T.surface}` },

  chip: { display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 20, background: T.bg, border: `1px solid ${T.border}`, fontSize: 11, fontWeight: 500, color: T.textSoft },

  messageBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 7, width: "100%", padding: "11px 0", borderRadius: 10, border: `1.5px solid ${T.border2}`, background: "transparent", color: T.textSoft, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.sans, transition: "all .15s" },

  calHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 },
  chevBtn:   { width: 30, height: 30, borderRadius: "50%", border: `1px solid ${T.border}`, background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  calMonth:  { fontSize: 13, fontWeight: 600, color: T.text },
  calGrid:   { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 },
  dayLabel:  { textAlign: "center", fontSize: 9, fontWeight: 600, color: T.muted, padding: "3px 0 8px", letterSpacing: "0.04em" },
  legend:    { display: "flex", gap: 14, marginTop: 12, justifyContent: "center" },
  legendItem: { display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: T.muted },
  legendDot: { width: 8, height: 8, borderRadius: "50%" },

  cancelledBanner: { display: "flex", alignItems: "center", gap: 10, background: T.bg, borderRadius: 10, padding: "12px 14px", fontSize: 13, color: T.muted, fontWeight: 500 },
  warningBox:      { display: "flex", gap: 10, alignItems: "flex-start", background: T.redLight, border: `1px solid ${T.redBorder}`, borderRadius: 10, padding: "12px 14px", marginBottom: 14 },
  warningText:     { fontSize: 12, color: "#922B21", lineHeight: 1.55 },
  cancelBtn:       { width: "100%", padding: "12px 0", borderRadius: 10, border: "none", background: T.red, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.02em" },

  textareaLabel:    { display: "block", fontSize: 11, fontWeight: 600, color: T.textSoft, marginBottom: 7, letterSpacing: "0.04em" },
  textarea:         { width: "100%", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px", fontSize: 13, color: T.text, resize: "vertical", outline: "none", marginBottom: 14, lineHeight: 1.5 },
  goBackBtn:        { flex: 1, padding: "12px 0", borderRadius: 10, border: `1.5px solid ${T.border2}`, background: "transparent", color: T.textSoft, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.sans },
  confirmCancelBtn: { flex: 2, padding: "12px 0", borderRadius: 10, border: "none", background: T.red, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.sans },
};